import { AppBar, Avatar, Box, Divider, Menu, MenuItem, Tab, Tabs, Toolbar, Tooltip } from "@mui/material";
import * as React from 'react';
import { Link, useNavigate, useLocation } from "react-router-dom";
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState } from "react";
import LogoutIcon from '@mui/icons-material/Logout';
import { useUserContext } from "@root/providers/user_context";
import { useUserProfileRedux } from "../../../hooks/useUserProfileRedux";
import { UserProfileCacheService } from "../../services/UserProfileCacheService";
import { SessionStorageService } from "../../services/SessionStorageService";

const NavBar = () => {
  const [value, setValue] = React.useState(0);
  const { signOut } = useUserContext();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { userProfile, loading, clearProfileCache } = useUserProfileRedux(); // Use Redux only
  const navigate = useNavigate();

  // Check if user has admin access
  const isAdmin = userProfile?.isAdmin ||
    userProfile?.roles?.some(role =>
      role.roleName.toLowerCase().includes('admin')
    );

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    // Handle tab changes, but ignore the admin tab (it's for dropdown only)
    const adminTabIndex = isAdmin ? 5 : -1;
    if (newValue !== adminTabIndex) {
      setValue(newValue);
    }
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [adminAnchorEl, setAdminAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const adminOpen = Boolean(adminAnchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAdminClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault(); // Prevent default tab behavior
    event.stopPropagation(); // Prevent tab selection
    setAdminAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAdminClose = () => {
    setAdminAnchorEl(null);
  };

  const handleProfileClick = () => {
    setAnchorEl(null);
    navigate('/profile');
  };

  const handleSettingsClick = () => {
    setAnchorEl(null);
    navigate('/settings');
  };

  // Handle logout with redirect to current domain root
  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      setAnchorEl(null); // Close the menu first

      // Clear all user profile caches (Redux and memory cache)
      clearProfileCache(); // Clear Redux cache
      UserProfileCacheService.clearCache(); // Clear memory cache

      // Use OIDC to sign out (this will handle the redirect and navigate away from page)
      // Note: This initiates redirect, so component will unmount - don't reset isLoggingOut
      signOut();

    } catch (error) {
      console.error('NavBar: Logout error, forcing manual cleanup', error);

      // Fallback: force logout and redirect
      clearProfileCache();
      UserProfileCacheService.clearCache();
      SessionStorageService.clearAll(); // Use SessionStorageService for proper cleanup
      sessionStorage.removeItem('userEmail');
      sessionStorage.removeItem('isLoggedIn');
      sessionStorage.removeItem('oidc_callback_processed');
      sessionStorage.removeItem('is_logging_out');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('isLoggedIn');
      window.location.href = window.location.origin;
    }
    // Note: No finally block - we're redirecting away, so no need to reset state
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      role="navigation"
      color="inherit"
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      <Toolbar sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8
              }
            }}
            onClick={() => navigate('/')}
          >
            <Box
              component="img"
              src='/RXO.svg'
              alt="MTFlow Logo"
              sx={{ height: 32, width: 'auto', mr: 2 }}
            />
            <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 32, alignSelf: 'center' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', ml: .5 }}>
              <span style={{ fontWeight: 600, fontSize: '1.25rem', letterSpacing: '0.05em', color: '#333' }} onClick={() => navigate('/')}>MTFlow</span>
            </Box>
          </Box>
          <Box sx={{ minWidth: 16 }} />

          <Tabs value={value} onChange={handleChange}>
            <Tab label="Onboarding" component={Link} to="/" />

            {isAdmin && (
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Admin
                    <ExpandMoreIcon fontSize="small" />
                  </Box>
                }
                onClick={handleAdminClick}
                sx={{
                  color: 'text.primary',
                  textTransform: 'none',
                  minHeight: 48,
                  borderBottom: adminOpen ? 2 : 0,
                  borderColor: 'primary.main',
                  borderRadius: 0
                }}
              />
            )}
          </Tabs>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge>
            <IconButton color="inherit">
              <NotificationsNoneIcon />
            </IconButton>
          </Badge>
          <Tooltip title="Profile" arrow>
            <IconButton onClick={handleClick} size="small" color="inherit">
              <Avatar alt="Avatar" variant="circular">
                {loading ? 'L' : (userProfile && (userProfile.firstName && userProfile.lastName) ?
                  (userProfile.firstName.charAt(0) + userProfile.lastName.charAt(0)).toUpperCase() : 'U')}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleClose}>
          {loading ? 'Loading...' : (userProfile && (userProfile.firstName && userProfile.lastName) ?
            userProfile.firstName + ' ' + userProfile.lastName : 'Unknown user')}
        </MenuItem>
        <MenuItem onClick={handleClose}>
          {loading ? 'Loading...' : (userProfile && userProfile.email ? userProfile.email : 'Unknown email')}
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleProfileClick}>
          <PersonIcon fontSize="small" sx={{ mr: 1 }} />
          Profile
        </MenuItem>
        <MenuItem onClick={handleSettingsClick}>
          <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
          Settings
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={handleLogout}
          disabled={isLoggingOut}
        >
          <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
          {isLoggingOut ? 'Logging out...' : 'Logout'}
        </MenuItem>
      </Menu>

      {/* Admin Dropdown Menu */}
      <Menu
        anchorEl={adminAnchorEl}
        open={adminOpen}
        onClose={handleAdminClose}
        onClick={handleAdminClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={() => { handleAdminClose(); navigate('/admin'); }}>
          Admin Dashboard
        </MenuItem>
        <MenuItem onClick={() => { handleAdminClose(); navigate('/admin/add-user'); }}>
          Add New User
        </MenuItem>
        <MenuItem onClick={() => { handleAdminClose(); navigate('/admin/all-users'); }}>
          All Users
        </MenuItem>
      </Menu>

    </AppBar>

  );
}

export default NavBar;
